export * from './fields';
export * from './shared';
import { Grid } from '@mui/material';
import { Form as FormikForm, Formik, FormikValues, FormikErrors } from 'formik';
import React, { FormEventHandler } from 'react';
import * as Yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { FormButtonArea } from './buttonArea';
import { FormField } from './fields';
import { Effect } from './shared/effect';
import { FormRef } from './shared/hooks/useForm';

export type FormProps = {
  children: FormField | Array<FormField>;
  initialValues?: FormikValues;
  onSubmit: (values: FormikValues) => void;
  onReset?: () => void;
  formRef?: FormRef;
  saveButtonText?: string;
  resetButtonText?: string;
  useCustomButton?: boolean;
  onChange?: FormEventHandler<HTMLFormElement>;
  onDirtyChanged?: (dirty: boolean) => void;
  onValidityChanged?: (valid: boolean) => void;
  validationSchema?: ObjectShape;
  enableReinitialize?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  validate?: (values: FormikValues) => FormikErrors<FormikValues> | Promise<void>;
};

/**
 * Form: A basic form component that renders children of type FormField
 * in a grid.
 *
 * It is not connected to a backend source. For this you should use the
 * `<UpdateForm />`.
 */
export const Form: (props: FormProps) => React.ReactElement = ({
  children,
  initialValues = {},
  onSubmit,
  onReset,
  formRef,
  saveButtonText,
  resetButtonText,
  useCustomButton,
  onChange,
  onDirtyChanged,
  onValidityChanged,
  validationSchema,
  enableReinitialize,
  validateOnBlur = true,
  validate
}) => {
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema && Yup.object().shape(validationSchema)}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      validateOnBlur={validateOnBlur}
      innerRef={formRef}
      enableReinitialize={enableReinitialize}
      validate={validate}
    >
      {({ handleReset }) => {
        return (
          <FormikForm noValidate onChange={onChange}>
            <Effect onDirtyChanged={onDirtyChanged} onValidityChanged={onValidityChanged} />
            <Grid container spacing={2}>
              {children}
              {useCustomButton ? (
                <SubmitOnEnterComponent />
              ) : (
                <FormButtonArea
                  saveButtonText={saveButtonText}
                  resetButtonText={resetButtonText}
                  onReset={() => {
                    handleReset();
                    onReset?.();
                  }}
                />
              )}
            </Grid>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

const SubmitOnEnterComponent = () => <input type={'submit'} hidden={true} />;
